/**
 * @file 教师管理页 · 类型与 mock
 * @description 教师列表本地模拟数据；科目对齐 `@/enum/common`。
 *
 * @whenToUse 仅教师管理页。
 * @whenNotToUse 其他业务模块。
 */

import { Subject, subjectMap } from '@/enum/common.ts'

/** 教师行 */
export type TeacherRow = {
  /** 教师 ID */
  id: string
  /** 教师名称 */
  name: string
  /** 教师账号 */
  account: string
  /** 授课科目（可多选） */
  subjects: string[]
  /** 学员人数 */
  studentCount: number
  /** 创建时间 */
  createdAt: string
  /** 是否启用 */
  enabled: boolean
}

/** 新建 / 编辑表单 */
export type TeacherFormValues = {
  /** 教师名称 */
  name: string
  /** 教师账号 */
  account: string
  /** 授课科目 */
  subjects: string[]
  /** 登录密码；新建可空表示默认 123456；编辑可空表示不改 */
  password: string
  /** 是否启用 */
  enabled: boolean
}

/** 列表筛选条件 */
export type TeacherQuery = {
  /** 教师名称（模糊） */
  name: string
  /** 启用状态；空为全部，`1` 启用，`0` 禁用 */
  enabled: string
}

export const EMPTY_TEACHER_FORM: TeacherFormValues = {
  name: '',
  account: '',
  subjects: [],
  password: '',
  enabled: true,
}

export const EMPTY_TEACHER_QUERY: TeacherQuery = {
  name: '',
  enabled: '',
}

/** 科目下拉选项 */
export const SUBJECT_OPTIONS = Object.entries(subjectMap).map(
  ([value, label]) => ({ value, label }),
)

/**
 * 科目 value 转展示文案
 * @param values 科目枚举值集合
 */
export function formatSubjects(values: string[]): string {
  if (values.length === 0) return '—'
  return values
    .map((value) => subjectMap[value as Subject] ?? value)
    .join('、')
}

/** 初始 mock 教师（学员人数与学员 mock 中绑定该教师的唯一学员数对齐） */
export const MOCK_TEACHERS: TeacherRow[] = [
  {
    id: 't-1',
    name: '王老师',
    account: 'wang_teacher',
    subjects: [Subject.MATH, Subject.PHYSICS],
    studentCount: 1,
    createdAt: '2025-10-12 09:30:00',
    enabled: true,
  },
  {
    id: 't-2',
    name: '刘老师',
    account: 'liu_teacher',
    subjects: [Subject.PHYSICS],
    studentCount: 1,
    createdAt: '2026-01-08 14:20:00',
    enabled: true,
  },
  {
    id: 't-3',
    name: '李老师',
    account: 'li_teacher',
    subjects: [Subject.CHEMISTRY],
    studentCount: 1,
    createdAt: '2026-03-22 11:05:00',
    enabled: false,
  },
  {
    id: 't-4',
    name: '张老师',
    account: 'zhang_teacher',
    subjects: [Subject.MATH, Subject.CHEMISTRY],
    studentCount: 0,
    createdAt: '2026-05-01 16:40:00',
    enabled: true,
  },
]

/**
 * 按查询条件过滤教师列表
 * @param rows 教师集合
 * @param query 筛选条件
 */
export function filterTeacherRows(
  rows: TeacherRow[],
  query: TeacherQuery,
): TeacherRow[] {
  return rows.filter((row) => {
    if (query.name && !row.name.includes(query.name.trim())) return false
    if (query.enabled === '1' && !row.enabled) return false
    if (query.enabled === '0' && row.enabled) return false
    return true
  })
}
